import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Tag, Avatar, Space, Typography, Divider } from 'antd';
import {
  PhoneOutlined,
  UserOutlined,
  TeamOutlined,
  DollarOutlined,
  UserAddOutlined,
  CloseOutlined,
  ArrowLeftOutlined,
  BankOutlined,
  SoundOutlined,
} from '@ant-design/icons';
import { useCallCenter } from '../../contexts/CallCenterContext';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const { Text, Title } = Typography;

const TYPE_META = {
  client: { label: 'مشتری', color: 'green', icon: <TeamOutlined />, path: (id) => `/clients/${id}` },
  lead: { label: 'سرنخ', color: 'orange', icon: <UserAddOutlined />, path: (id) => `/leads/${id}` },
  deal: { label: 'معامله', color: 'blue', icon: <DollarOutlined />, path: (id) => `/deals/${id}` },
};

const MatchChip = ({ item, type, onNavigate, onClose }) => {
  const meta = TYPE_META[type] || TYPE_META.client;
  const name = item.name || item.client_name || '—';
  const sub = item.mobile || item.phone || item.email || '';

  return (
    <button
      type="button"
      onClick={() => {
        if (item.id) {
          onNavigate(meta.path(item.id));
          onClose();
        }
      }}
      className="w-full text-right flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 hover:border-teal-300 dark:hover:border-teal-600 hover:shadow-md transition-all group"
    >
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-${meta.color === 'green' ? 'emerald' : meta.color}-50 dark:bg-gray-700 text-${meta.color === 'green' ? 'emerald' : meta.color}-600`}
        style={{
          background: type === 'client' ? 'rgba(16,185,129,0.12)' : type === 'lead' ? 'rgba(249,115,22,0.12)' : 'rgba(59,130,246,0.12)',
          color: type === 'client' ? '#059669' : type === 'lead' ? '#ea580c' : '#2563eb',
        }}
      >
        {meta.icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="custom-font font-medium text-gray-800 dark:text-gray-100 truncate">{name}</div>
        {sub && (
          <div className="custom-font text-xs text-gray-500 truncate" dir="ltr">
            {sub}
          </div>
        )}
      </div>
      <Tag color={meta.color} className="custom-font m-0 shrink-0">
        {meta.label}
      </Tag>
      <ArrowLeftOutlined className="text-gray-300 group-hover:text-teal-500 transition-colors shrink-0" />
    </button>
  );
};

const CallScreenPopModal = () => {
  const navigate = useNavigate();
  const { screenPopOpen, screenPopData, closeScreenPop } = useCallCenter();

  const phone = screenPopData?.phone || screenPopData?.call?.phone || '';
  const primary = screenPopData?.match?.primary;
  const match = screenPopData?.match || {};
  const call = screenPopData?.call;

  const primaryMeta = primary?.type ? TYPE_META[primary.type] : null;

  const otherMatches = useMemo(() => {
    const items = [];
    (match.clients || []).forEach((c) => {
      if (!(primary?.type === 'client' && primary?.id === c.id)) {
        items.push({ item: c, type: 'client' });
      }
    });
    (match.leads || []).forEach((l) => {
      if (!(primary?.type === 'lead' && primary?.id === l.id)) {
        items.push({ item: l, type: 'lead' });
      }
    });
    (match.deals || []).forEach((d) => {
      if (!(primary?.type === 'deal' && primary?.id === d.id)) {
        items.push({ item: d, type: 'deal' });
      }
    });
    return items;
  }, [match, primary]);

  useEffect(() => {
    if (!screenPopOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeScreenPop();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [screenPopOpen, closeScreenPop]);

  const handleViewPrimary = () => {
    if (primary?.type && primary?.id && primaryMeta) {
      navigate(primaryMeta.path(primary.id));
      closeScreenPop();
    }
  };

  const handleViewCall = () => {
    if (call?.id) {
      navigate(`/call-center/calls/${call.id}`);
      closeScreenPop();
    }
  };

  return (
    <Modal
      open={screenPopOpen}
      onCancel={closeScreenPop}
      footer={null}
      closable={false}
      width={480}
      centered
      destroyOnClose
      className="call-screen-pop-modal"
      styles={{
        mask: { backdropFilter: 'blur(4px)', background: 'rgba(7, 11, 20, 0.55)' },
        content: { padding: 0, borderRadius: 20, overflow: 'hidden', boxShadow: '0 25px 60px rgba(13, 136, 126, 0.25)' },
      }}
    >
      <div className="relative overflow-hidden" style={{ fontFamily: 'CustomFont' }}>
        {/* هدر با انیمیشن زنگ */}
        <div className="relative bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 px-6 pt-8 pb-10 text-white">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-4 right-8 w-24 h-24 rounded-full bg-white animate-ping" style={{ animationDuration: '2s' }} />
            <div className="absolute top-8 right-12 w-16 h-16 rounded-full bg-white/40 animate-pulse" />
          </div>

          <button
            type="button"
            onClick={closeScreenPop}
            className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center border-0 cursor-pointer text-white transition-colors z-10"
            aria-label="بستن"
          >
            <CloseOutlined />
          </button>

          <div className="relative z-10 flex flex-col items-center text-center">
            <div className="relative mb-4">
              <div className="absolute inset-0 rounded-full bg-white/30 animate-ping" style={{ animationDuration: '1.5s' }} />
              <div className="relative w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/40 shadow-xl">
                <PhoneOutlined className="text-4xl" style={{ transform: 'rotate(-15deg)' }} />
              </div>
            </div>
            <Tag
              color="processing"
              className="custom-font mb-2 border-0 bg-white/20 text-white"
              icon={<SoundOutlined />}
            >
              {call?.event === 'ringing' ? 'تماس ورودی — در حال زنگ خوردن' : 'تماس ورودی'}
            </Tag>
            <Title level={2} className="!text-white !m-0 !font-bold tracking-wide" dir="ltr">
              {phone || '—'}
            </Title>
            {call?.extension && (
              <Text className="!text-white/80 text-sm mt-1 block" dir="ltr">
                داخلی {call.extension}
              </Text>
            )}
          </div>
        </div>

        {/* بدنه */}
        <div className="px-5 py-5 -mt-4 relative z-20">
          {primary ? (
            <div className="rounded-2xl border-2 border-teal-200 dark:border-teal-800 bg-gradient-to-b from-white to-teal-50/50 dark:from-gray-900 dark:to-teal-950/30 shadow-lg p-5 mb-4">
              <div className="flex items-start gap-4">
                <Avatar
                  size={56}
                  className="shrink-0 bg-gradient-to-br from-teal-500 to-emerald-600 text-white font-bold text-xl"
                  icon={!primary.name ? <UserOutlined /> : undefined}
                >
                  {(primary.name || '?').charAt(0)}
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="custom-font text-lg font-bold text-gray-900 dark:text-white truncate">
                      {primary.name || '—'}
                    </span>
                    {primaryMeta && (
                      <Tag color={primaryMeta.color} className="custom-font m-0">
                        {primaryMeta.label}
                      </Tag>
                    )}
                  </div>
                  {primary.company_name && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 custom-font mb-1">
                      <BankOutlined className="text-xs" />
                      <span>{primary.company_name}</span>
                    </div>
                  )}
                  {primary.mobile && (
                    <div className="text-sm text-gray-500 custom-font" dir="ltr">
                      {primary.mobile}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {primaryMeta && primary.id && (
                  <Button
                    type="primary"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleViewPrimary}
                    className={PANEL_PRIMARY_BTN_CLASS}
                  >
                    مشاهده {primaryMeta.label}
                  </Button>
                )}
                {call?.id && (
                  <Button onClick={handleViewCall} className="custom-font">
                    جزئیات تماس
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 text-center mb-4">
              <UserOutlined className="text-3xl text-gray-300 mb-2" />
              <p className="custom-font text-gray-500 m-0 text-sm">
                مخاطبی در CRM یافت نشد
              </p>
              <p className="custom-font text-gray-400 m-0 mt-1 text-xs" dir="ltr">
                {phone}
              </p>
            </div>
          )}

          {otherMatches.length > 0 && (
            <>
              <Divider className="!my-3">
                <span className="custom-font text-xs text-gray-400">سایر تطبیق‌ها</span>
              </Divider>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {otherMatches.map(({ item, type }) => (
                  <MatchChip
                    key={`${type}-${item.id}`}
                    item={item}
                    type={type}
                    onNavigate={navigate}
                    onClose={closeScreenPop}
                  />
                ))}
              </div>
            </>
          )}

          <div className="mt-5 flex justify-center">
            <Button
              size="large"
              onClick={closeScreenPop}
              className="custom-font min-w-[140px] rounded-xl"
            >
              بستن
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CallScreenPopModal;
